import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Article from "./Article";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

function DailyMetrics({ statsData, onViewDetails }) {
    const [metrics, setMetrics] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosPrivate.get("/daily_metrics", {headers: {Authorization: "Bearer " + auth.accessToken}});
                setMetrics(response.data);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            }
        };
        fetchData();
    }, [axiosPrivate, auth.accessToken]);

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = useRef(null);

    useEffect(() => {
        if (selectedArticleRef.current) {
            selectedArticleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500">Details for {selectedArticle.title}</p>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <h3 className="text-lg font-medium mb-4">Performance Metrics</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={[...metrics].reverse()} margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="daily_active_users" stroke="#8884d8" activeDot={{r: 8}} name="Daily Active Users"/>
                                    <Line type="monotone" dataKey="weekly_active_users" stroke="#82ca9d" activeDot={{r: 6}} name="Weekly Active Users"/>
                                    <Line type="monotone" dataKey="monthly_active_users" stroke="#ff7300" activeDot={{r: 4}} name="Monthly Active Users"/>
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    )
}

export default DailyMetrics;